/**
 * 用語の一括書込（API §3.2 の `upsert_spec_term`）— 作り直しの形。
 *
 * 旧経路との違いは 3 つである。
 *
 * 1. **一括になる。** 値の用語が全部新規になり（今 0 件）、プロパティ 1 つにつき値の用語が
 *    2〜数個要る。1 件ずつでは往復が増えすぎる（API §3.1）。
 * 2. **欄が替わる。** 語の種別 4 値は、区画 3 × 表現の型 10 に置き換わった（NE §1-attrs）。
 *    どの欄が必須でどの欄が書けないかは、区画と表現の型で機械的に決まる（契約の判別ユニオン）。
 * 3. **動詞はここで書かない。** 用語から出てマスター（`Verb`）になった（`masters.ts`）。
 *
 * **工程 1 の順（クラス → 値 → プロパティ）を 1 回の呼び出しで書ける。** プロパティは値と
 * クラスを指すので、先に在るべきものが同じ呼び出しの中にある。そのために新しい用語へ仮の番号
 * （負の数）を付け、後ろの要素の欄からその番号で指す（API §3.0）。
 *
 * **用語の変更は承認制である。** 案件の変更の行が `pending` で生まれ（F7a の
 * `initialPjChangeStatus`）、1 つでも残る案件は確定できない（制約 31）。承認はコンソールから
 * だけで、MCP からはできない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の一括書込。区画と表現の型の欄を受け、仮の番号でクラス → 値 → プロパティを 1 回の呼び出しで書く
 * @graph-connects none
 */
import { z } from 'zod';
import { specPjNumberValue, termElementSchema } from '../../contract/elements/index.js';
import { noteText } from '../../contract/node-schemas.js';
import { WRITE_BATCH_LIMIT } from '../../contract/operations.js';
import { composeElementSnapshot } from './pj-changes.js';
import { applyElementWrite, elementRequirementSchema, elementWriteTargetIdSchema, rejectElementWrite, toInputSchemaRejections, } from './write.js';
/**
 * 用語の欄の形（契約の入力スキーマと、欄の中の参照へ至る道）。
 *
 * 参照は 4 つの欄に現れる。`values` と `ownerClass` と `unit`、それに状態遷移の 3 つ組である。
 * 状態遷移の `from` / `to` は値の用語、`event` は動詞を指す — 3 つ組の中で型が違うので、道を
 * 1 本ずつ宣言する。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の入力スキーマと、欄の中の参照へ至る道の宣言
 * @graph-connects none
 */
export const TERM_ELEMENT_SPEC = {
    schema: termElementSchema,
    references: [
        { path: ['ownerClass'], expect: { nodeType: 'Term', section: 'class' } },
        { path: ['values', '*'], expect: { nodeType: 'Term', section: 'value' } },
        { path: ['unit'], expect: { nodeType: 'Unit' } },
        { path: ['transitions', '*', 'from'], expect: { nodeType: 'Term', section: 'value' } },
        { path: ['transitions', '*', 'event'], expect: { nodeType: 'Verb' } },
        { path: ['transitions', '*', 'to'], expect: { nodeType: 'Term', section: 'value' } },
    ],
};
/**
 * 用語の一括書込の入力。
 *
 * 上限は `WRITE_BATCH_LIMIT` で、一括の書込すべてに同じ値が当たる（API §3.1）。性能の都合では
 * なく品質の機構である — 一度に大量に生成させると後半ほど雑になるので、1 回で作る量を人が目で
 * 追える大きさに抑える。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の一括書込の入力スキーマ（案件番号・検査だけか・書く用語の並び）
 * @graph-connects none
 */
export const termElementWriteInputSchema = z.strictObject({
    pjNumber: specPjNumberValue(),
    dryRun: z.boolean(),
    operations: z
        .array(z.strictObject({
        requirement: elementRequirementSchema,
        /** 省略 = 新規。負の数はこの呼び出しの中だけで通じる仮の番号 */
        id: elementWriteTargetIdSchema.optional(),
        /** 用語 1 件の欄 */
        element: z.unknown(),
    }))
        .min(1)
        .max(WRITE_BATCH_LIMIT),
});
/**
 * 用語をまとめて書く。
 *
 * 並びの順に適用するので、前の要素が作った用語を後ろの要素が仮の番号で指せる。**1 件でも検査に
 * 落ちれば 1 行も書かない** — 途中まで書いて残りを拒否すると、呼び出し側が「どこまで入ったか」
 * を知る手段を持たない。
 *
 * @param raw 入力（形式検査の前）
 * @param actor 認証済みの書込主体
 * @param deps 置き場・採番・master の読取・適用時刻
 * @returns 適用 / 検査だけ / 拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の一括書込を、作り直しの書込の道（採番 → 検査 → 2 表への書き分け）に通す
 * @graph-connects applyElementWrite [calls] 書込 1 回分の適用
 */
export async function writeSpecTermElements(raw, actor, deps) {
    const parsed = termElementWriteInputSchema.safeParse(raw);
    if (!parsed.success) {
        return { outcome: 'rejected', rejections: toInputSchemaRejections('', parsed.error) };
    }
    const input = parsed.data;
    const noteIndex = input.operations.findIndex((operation) => hasNoteField(operation.element));
    if (noteIndex >= 0) {
        return rejectElementWrite('input-schema', `operations.${String(noteIndex)}.element.note`, '備考（note）は画面からだけ書ける。この入口では送らない（今の備考は書き直しても残る）');
    }
    return applyElementWrite({
        pjNumber: input.pjNumber,
        dryRun: input.dryRun,
        actor,
        writeKind: 'write',
        items: input.operations.map((operation) => ({
            operation: 'upsert',
            nodeType: 'Term',
            requirementId: operation.requirement.requirementId,
            id: operation.id,
            payload: operation.element,
            spec: TERM_ELEMENT_SPEC,
            preservedFields: TERM_SCREEN_ONLY_FIELDS,
        })),
        deps,
    });
}
/**
 * 画面からだけ書ける用語の欄。用語の一括書込（MCP の入口）は受けず、書き直しても今の値を残す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 人が画面からだけ書く用語の欄（備考）の閉集合
 * @graph-connects none
 */
export const TERM_SCREEN_ONLY_FIELDS = ['note'];
/**
 * 欄のかたまりに備考（`note`）の欄があるか。
 * @graph-connects none
 */
function hasNoteField(element) {
    return element !== null && typeof element === 'object' && 'note' in element;
}
/**
 * 用語の備考を書く入力。`note` が null なら備考を消す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の備考を画面から書く入力スキーマ（案件番号・要求・用語の番号・備考）
 * @graph-connects none
 */
export const termNoteWriteInputSchema = z.strictObject({
    pjNumber: specPjNumberValue(),
    requirementId: elementRequirementSchema.shape.requirementId,
    termId: elementWriteTargetIdSchema.refine((id) => id > 0, {
        message: '備考は既にある用語にだけ書ける',
    }),
    note: noteText().nullable(),
});
/**
 * 用語の備考を書く（画面の入口）。
 *
 * **AI は書けない欄なので、MCP にはこの入口を置かない**（CLAUDE.md「操作は画面と MCP の両方に
 * 置く」の例外。備考は人が書く補足で、書き手を人に限るのが欄の目的である）。読み取りは MCP でも
 * できる。
 *
 * 今の値（master ⊕ 案件の姿）に備考だけを重ねて、用語 1 件の書き直しとして通す。ほかの欄は
 * 今の値のまま変わらない。用語の変更なので、案件の変更の行は承認待ちで生まれる。
 *
 * @param raw 入力（形式検査の前）
 * @param actor 認証済みの書込主体
 * @param deps 置き場・採番・master の読取・適用時刻
 * @returns 適用 / 拒否
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の備考を、今の値に重ねた用語 1 件の書き直しとして案件へ書く
 * @graph-connects applyElementWrite [calls] 書込 1 回分の適用
 */
export async function writeSpecTermNote(raw, actor, deps) {
    const parsed = termNoteWriteInputSchema.safeParse(raw);
    if (!parsed.success) {
        return { outcome: 'rejected', rejections: toInputSchemaRejections('', parsed.error) };
    }
    const input = parsed.data;
    const master = await deps.readMaster([input.termId]);
    const changes = await deps.store.listPjChanges(input.pjNumber);
    const current = composeElementSnapshot(master, changes, () => []).nodes.get(input.termId);
    if (current === undefined) {
        return rejectElementWrite('target-not-found', 'termId', `用語 ${String(input.termId)} はこの案件から見えない`);
    }
    if (current.nodeType !== 'Term') {
        return rejectElementWrite('target-type-mismatch', 'termId', `ノード ${String(input.termId)} は ${current.nodeType} で、備考は用語にだけ書ける`);
    }
    const rest = Object.fromEntries(Object.entries(current.properties).filter(([field]) => field !== 'note'));
    const payload = input.note === null ? rest : { ...rest, note: input.note };
    return applyElementWrite({
        pjNumber: input.pjNumber,
        dryRun: false,
        actor,
        writeKind: 'write',
        items: [
            {
                operation: 'upsert',
                nodeType: 'Term',
                requirementId: input.requirementId,
                id: input.termId,
                payload,
                spec: TERM_ELEMENT_SPEC,
            },
        ],
        deps,
    });
}
//# sourceMappingURL=terms.js.map